/**
 * The "All inquiry" list — the last week's sign-ups who have not booked yet.
 *
 * Membership is two facts: the patient is still at the Inquiry stage, and they
 * signed up within the window. The window is fixed at seven days on purpose: the
 * list answers "who came in this week that nobody has reached", and a scope
 * control would turn it back into the Inquiry bucket — the largest population in
 * the project, and the one the stage-tag path cannot fetch.
 */

import dayjs from 'dayjs';
import { getExtension } from '@medplum/core';
import { Patient } from '@medplum/fhirtypes';
import { BariatricProcedureStage, BARIATRIC_PROCEDURE_STAGE_TAG_SYSTEM, Track, TrackLabel } from './constants';
import { BARIATRIC_PROCEDURE_STAGE_EXTENSION, EMPLOYER_EXTENSION } from './extensions';
import { getMetaTag } from './meta-tags';

export const INQUIRY_WINDOW_DAYS = 7;

/** `YYYY-MM-DD` of the first day in the window, today included. */
export function inquiryWindowStart(now: Date, days = INQUIRY_WINDOW_DAYS): string {
  return dayjs(now)
    .subtract(days - 1, 'day')
    .format('YYYY-MM-DD');
}

/**
 * The stored stage code, tag first then the extension — the same precedence as
 * `getBariatricProcedureStageLabel` in Orbit.
 */
export function readStageCode(patient: Patient): string | undefined {
  return (
    getMetaTag(patient, BARIATRIC_PROCEDURE_STAGE_TAG_SYSTEM)?.code ??
    getExtension(patient, BARIATRIC_PROCEDURE_STAGE_EXTENSION)?.valueString
  );
}

/**
 * Inquiry, or not yet staged.
 *
 * The stage is a cache written by the `update-procedure-status` bot, and a
 * patient who signed up minutes ago may not carry it yet. Unstaged reads as
 * Inquiry here because Inquiry is what the bot would write for them — every
 * other stage requires an appointment or a payment, both of which trigger the
 * write. Excluding them would drop exactly the freshest leads from a list
 * about fresh leads.
 */
export function isInquiryStage(patient: Patient): boolean {
  const stage = readStageCode(patient);
  return stage === undefined || stage === BariatricProcedureStage.PRE_CONSULTATION_UNPAID;
}

/** Signed up on or after the window start. Unknown sign-up date is out. */
export function isSignedUpWithinWindow(signupAt: string | undefined, now: Date, days = INQUIRY_WINDOW_DAYS): boolean {
  if (!signupAt) {
    return false;
  }
  return !dayjs(signupAt).isBefore(dayjs(inquiryWindowStart(now, days)), 'day');
}

export function isInquiryLead(lead: { patient: Patient; signupAt?: string }, now: Date): boolean {
  return isInquiryStage(lead.patient) && isSignedUpWithinWindow(lead.signupAt, now);
}

export function readEmployer(patient: Patient): string | undefined {
  const value = getExtension(patient, EMPLOYER_EXTENSION)?.valueString;
  return value && value.length > 0 ? value : undefined;
}

const PARTNER_TRACKS: ReadonlySet<Track> = new Set([Track.CARRUM, Track.TRANSCARENT]);

/**
 * The partner tracks plus `employer` — every lead whose conversation is about
 * employer coverage, verified or not (BAR-2596).
 *
 * Kept apart from PARTNER_TRACKS because the two answer different questions.
 * "Is an employer in this conversation?" decides what the inquiry list says.
 * "Is anyone but the patient paying?" (`collectsPayment` in constants.ts)
 * decides whether the Intake & billing panel replaces the retail/discount/net
 * chain with "Covered by" — and doing that to an unverified lead would hide the
 * numbers from the rep who is quoting them.
 */
const EMPLOYER_SPONSORED_TRACKS: ReadonlySet<Track> = new Set([...PARTNER_TRACKS, Track.EMPLOYER]);

const EMPLOYER_BENEFIT = 'Employer benefit';

const INSURANCE = TrackLabel[Track.INSURANCE];

/**
 * A verified partner is paying. `Track.EMPLOYER` is NOT one — that benefit is
 * self-reported and the lead may still pay retail. Use
 * {@link isEmployerSponsoredTrack} for anything that only needs to know an
 * employer is in the picture.
 */
export function isPartnerTrack(track: Track | undefined): boolean {
  return track !== undefined && PARTNER_TRACKS.has(track);
}

/** An employer is expected to contribute, whether or not that is confirmed. */
export function isEmployerSponsoredTrack(track: Track | undefined): boolean {
  return track !== undefined && EMPLOYER_SPONSORED_TRACKS.has(track);
}

export interface PaymentMethodCellInput {
  /** The `pay` answer from the intake card, when one has been saved. */
  paymentMethod?: string;
  track?: Track;
  /**
   * From `EMPLOYER_EXTENSION`. Written at sign-up for Carrum patients and, since
   * BAR-2596, for any lead who typed an employer on the eligibility flow.
   */
  employer?: string;
  /**
   * The insurer: the name of the payor Organization on the lead's insurance
   * Coverage. Read only on the insurance track.
   */
  payer?: string;
}

/**
 * Is the lead's own health insurance paying? The track alone decides it, and it
 * outranks any payment answer: an insurance patient is never charged.
 */
export function isInsurancePayment({ track }: PaymentMethodCellInput): boolean {
  return track === Track.INSURANCE;
}

/**
 * Is this lead expecting an employer to pay? True on the answer itself
 * (rep-confirmed or self-reported) or on any employer-sponsored track — the
 * same rule the cell renders by, so the chip and the cell can never disagree.
 * Never on the insurance track, whatever the answer: insurance is not employer
 * coverage, and the cell reads "Insurance" there.
 */
export function isEmployerBenefitPayment({ paymentMethod, track }: PaymentMethodCellInput): boolean {
  if (isInsurancePayment({ track })) {
    return false;
  }
  return paymentMethod === EMPLOYER_BENEFIT || isEmployerSponsoredTrack(track);
}

/**
 * "Insurance · Aetna" on the insurance track; "Employer benefit · Carrum Health"
 * for a B2B lead; the bare answer otherwise.
 *
 * A B2B patient with no card yet still reads `Employer benefit · <track>`: the
 * track is known at sign-up, and a rep triaging inquiries needs to know the
 * conversation is about coverage rather than financing before the first call,
 * not after it. The employer name outranks the track label because it is the
 * more specific of the two; the label is the fallback when the extension is
 * empty.
 */
export function formatPaymentMethodCell({
  paymentMethod,
  track,
  employer,
  payer,
}: PaymentMethodCellInput): string | undefined {
  // No payer until signup writes the insurer onto the Coverage (BAR-2905).
  if (isInsurancePayment({ track })) {
    return payer ? `${INSURANCE} · ${payer}` : INSURANCE;
  }

  const b2b = isEmployerSponsoredTrack(track);
  const base = paymentMethod ?? (b2b ? EMPLOYER_BENEFIT : undefined);

  if (!base) {
    return undefined;
  }

  if (base !== EMPLOYER_BENEFIT && !b2b) {
    return base;
  }

  const sponsor = employer ?? (track && b2b ? TrackLabel[track] : undefined);
  return sponsor ? `${base} · ${sponsor}` : base;
}
